import { Vehicle, VehicleRequest } from "../../types/vehicle.type";

export interface VehicleForm {
  vehicle_plate: string;
  vehicle_type: string;
  model: string;
  capacity: string;
  fuel_type: string;
}

export type VehicleFormErrors = Partial<Record<keyof VehicleForm, string>>;

export const INITIAL_VEHICLE_FORM: VehicleForm = {
  vehicle_plate: "",
  vehicle_type: "",
  model: "",
  capacity: "",
  fuel_type: "",
};

/** Longest vehicle type / model the API accepts (both columns are VARCHAR(50)). */
export const VEHICLE_TEXT_MAX_LENGTH = 50;

/** The API's plate format: an optional leading digit, two Thai consonants, a space, then 1–4 digits. */
const VEHICLE_PLATE_PATTERN = /^\d?[ก-ฮ]{2} \d{1,4}$/;
const MIN_CAPACITY_LITRES = 1;

export const VEHICLE_PLATE_EXAMPLE = "ตัวอย่าง: 9กก 1234, กก 1234";

/** The same fuel keys the PWA stores, so a vehicle added on either app reads the same. */
export const FUEL_TYPE_OPTIONS = [
  { value: "gasoline", label: "เบนซิน" },
  { value: "diesel", label: "ดีเซล" },
  { value: "ev", label: "ไฟฟ้า" },
  { value: "hybrid", label: "ไฮบริด" },
  { value: "gas", label: "ก๊าซ" },
];

/** A stored fuel key in Thai; a value typed before the keys existed is shown as stored. */
export const fuelTypeLabel = (fuelType: string | null): string =>
  FUEL_TYPE_OPTIONS.find((option) => option.value === fuelType)?.label ??
  fuelType ??
  "";

export function validateVehicleForm(form: VehicleForm): VehicleFormErrors {
  const errors: VehicleFormErrors = {};
  const plate = form.vehicle_plate.trim();
  const capacity = form.capacity.trim();

  if (!plate) {
    errors.vehicle_plate = "กรุณากรอกป้ายทะเบียน";
  } else if (!VEHICLE_PLATE_PATTERN.test(plate)) {
    errors.vehicle_plate = `รูปแบบป้ายทะเบียนไม่ถูกต้อง (${VEHICLE_PLATE_EXAMPLE})`;
  }
  if (capacity && !(Number(capacity) >= MIN_CAPACITY_LITRES)) {
    errors.capacity = "ปริมาตรถังน้ำมันต้องเป็นตัวเลขตั้งแต่ 1 ขึ้นไป";
  }
  return errors;
}

const blankToNull = (value: string): string | null => value.trim() || null;

export function toVehicleRequest(form: VehicleForm): VehicleRequest {
  const capacity = form.capacity.trim();
  return {
    vehicle_plate: form.vehicle_plate.trim(),
    vehicle_type: blankToNull(form.vehicle_type),
    model: blankToNull(form.model),
    capacity: capacity ? Number(capacity) : null,
    fuel_type: blankToNull(form.fuel_type),
  };
}

export function toVehicleForm(vehicle: Vehicle): VehicleForm {
  return {
    vehicle_plate: vehicle.vehicle_plate,
    vehicle_type: vehicle.vehicle_type ?? "",
    model: vehicle.model ?? "",
    capacity: vehicle.capacity === null ? "" : String(Number(vehicle.capacity)),
    fuel_type: vehicle.fuel_type ?? "",
  };
}
